// ─────────────────────────────────────────────────────────────────────────────
// SITUATIONAL LEADERSHIP — Factors
// Hersey-Blanchard model: 4 leadership styles x 4 follower dev levels
// ─────────────────────────────────────────────────────────────────────────────

export const STYLES = [
  {
    id:       "S1",
    name:     "Directing",
    alias:    "Telling / Guiding",
    icon:     "🎯",
    color:    "#dc2626",
    bg:       "#fef2f2",
    task:     "High",
    rel:      "Low",
    match:    "D1",
    headline: "High directive · Low supportive",
    description: "The leader defines the what, when and how. Provides step-by-step instructions and monitors closely. Most effective with followers who are new to a task, enthusiastic but inexperienced.",
    when: [
      "Follower is D1 — new to the task with low competence",
      "Crisis requiring fast, unambiguous execution",
      "Safety-critical tasks with zero error tolerance",
      "Compliance requirements with no latitude for interpretation",
    ],
    behaviours: [
      "Define the task, method, timeline and expected outcome explicitly",
      "Give step-by-step instructions with rationale",
      "Supervise closely and check in frequently",
      "Provide immediate, specific corrective feedback",
    ],
    soundsLike: "Here is exactly what I need you to do, and here is how to do it. Check in with me after every step until you are comfortable.",
    overUseCost: "Micromanagement. Follower never develops autonomy. Leader becomes a bottleneck. Capable staff become resentful and disengaged.",
  },
  {
    id:       "S2",
    name:     "Coaching",
    alias:    "Selling / Explaining",
    icon:     "🧭",
    color:    "#d97706",
    bg:       "#fffbeb",
    task:     "High",
    rel:      "High",
    match:    "D2",
    headline: "High directive · High supportive",
    description: "The leader still directs but explains the rationale behind decisions, invites questions, and builds confidence. Combines structure with emotional encouragement. Best for followers who are developing but losing motivation.",
    when: [
      "Follower is D2 — some skill but declining confidence or motivation",
      "Development conversations with a struggling employee",
      "Rebuilding commitment after setbacks or failure",
      "Moving a follower from D1 toward D3",
    ],
    behaviours: [
      "Explain the reasoning behind decisions and methods",
      "Invite two-way dialogue and genuine questions",
      "Acknowledge effort, not just outcomes",
      "Praise visible progress; correct course with encouragement",
    ],
    soundsLike: "I am going to give you clear direction on this because I know it is still new — but I want to understand where you are getting stuck. What part feels unclear to you?",
    overUseCost: "Creates dependency. Employee never moves to D3 because the leader keeps rescuing. Can feel patronising to high-competence followers who do not need the direction.",
  },
  {
    id:       "S3",
    name:     "Supporting",
    alias:    "Participating / Facilitating",
    icon:     "🤝",
    color:    "#2563eb",
    bg:       "#eff6ff",
    task:     "Low",
    rel:      "High",
    match:    "D3",
    headline: "Low directive · High supportive",
    description: "The follower drives; the leader facilitates. Decision-making is shared. Best for technically capable followers who lack the confidence to act without reassurance. The leader's job is to affirm capability, not provide direction.",
    when: [
      "Follower is D3 — high skill but variable or low confidence",
      "Employee has the ability but hesitates to own decisions",
      "Stretch assignments for a proven but self-doubting performer",
      "Team member seeks approval on decisions within their authority",
    ],
    behaviours: [
      "Ask more than you tell — use questions to surface the follower's thinking",
      "Listen actively and reflect capability back to the follower",
      "Praise competence visibly and specifically",
      "Facilitate the decision; do not make it for them",
    ],
    soundsLike: "You have done this before and done it well. What is your instinct on how to approach this? I will back whatever decision you make — let me know if you want to think it through together.",
    overUseCost: "Abdication disguised as empowerment. Accountability becomes murky. D1 followers who receive S3 feel abandoned; they need direction, not facilitation.",
  },
  {
    id:       "S4",
    name:     "Delegating",
    alias:    "Empowering / Observing",
    icon:     "🚀",
    color:    "#059669",
    bg:       "#f0fdf4",
    task:     "Low",
    rel:      "Low",
    match:    "D4",
    headline: "Low directive · Low supportive",
    description: "The follower owns the task fully. The leader defines the outcome, sets accountability, and monitors at milestones only. Most effective with expert, self-motivated followers who thrive on autonomy. Over-management of D4 followers is a significant retention risk.",
    when: [
      "Follower is D4 — expert in the task with high self-motivation",
      "Trusted team lead or specialist on familiar territory",
      "Long-standing high performer with proven judgment",
      "The follower's method will be better than yours",
    ],
    behaviours: [
      "Define the outcome and timeline; not the method",
      "Set clear accountability and success metrics",
      "Check in at milestones only — not task level",
      "Celebrate results publicly; remove systemic blockers quietly",
    ],
    soundsLike: "I know you have got this. Here is the outcome we are after and the timeline. Come to me if you hit a wall — otherwise it is yours.",
    overUseCost: "D4 with a new challenge regresses to D1 and needs you back. Mistaking seniority of title for task-specific readiness creates the most damaging leadership failures.",
  },
];

export const STYLE_MAP = Object.fromEntries(STYLES.map(s => [s.id, s]));

export const DEV_LEVELS = [
  {
    id:       "D1",
    name:     "Enthusiastic Beginner",
    competence:  "Low",
    commitment:  "High",
    icon:        "⚡",
    color:       "#dc2626",
    matchStyle:  "S1",
    description: "New to the task. Often eager, motivated and willing — but lacks the knowledge or skill to execute well without guidance. May not know what they do not know.",
    signals: [
      "Asks many questions — or assumes they know and asks none",
      "Enthusiastic about the task but output falls short of expectations",
      "Does not anticipate problems or risks they have not encountered yet",
      "High confidence that is not yet earned through experience",
    ],
    misreadRisk: "Over-empowered (treated as D4) — fails publicly and loses the confidence that made them valuable.",
  },
  {
    id:       "D2",
    name:     "Disillusioned Learner",
    competence:  "Some",
    commitment:  "Low",
    icon:        "😤",
    color:       "#d97706",
    matchStyle:  "S2",
    description: "Has started to develop skills but encountered difficulty. Motivation and confidence have dropped. Often frustrated with the gap between expectation and reality.",
    signals: [
      "Competence visible but inconsistent — good days and bad days",
      "Withdraws, complains, or cynically comments on the process",
      "Over-checks with the leader or avoids asking for help out of pride",
      "Expresses frustration with the role, the task, or colleagues",
    ],
    misreadRisk: "Under-supported (treated as D3 or D4) — disengages further or leaves. The silent D2 is the most at-risk profile.",
  },
  {
    id:       "D3",
    name:     "Capable but Cautious",
    competence:  "High",
    commitment:  "Variable",
    icon:        "🤔",
    color:       "#2563eb",
    matchStyle:  "S3",
    description: "Technically capable and often high-performing — but lacks full confidence to work without some level of reassurance. May seem inconsistent or seek unnecessary approval.",
    signals: [
      "Seeks sign-off on decisions that are clearly within their authority",
      "Qualifies their own work before you have reviewed it",
      "Performs well when supported; inconsistent when left alone",
      "Expresses doubt about their own judgment despite a track record of success",
    ],
    misreadRisk: "Under-supported (treated as D4) — signals distrust, not efficiency. The expert who quietly stops trying.",
  },
  {
    id:       "D4",
    name:     "Self-Reliant Achiever",
    competence:  "High",
    commitment:  "High",
    icon:        "🏆",
    color:       "#059669",
    matchStyle:  "S4",
    description: "Expert in the task. Self-motivated, confident, and capable of managing themselves and others. Thrives with autonomy. Over-management will demotivate them significantly.",
    signals: [
      "Delivers consistently without prompting or close oversight",
      "Anticipates problems and solves them before escalating",
      "Operates within their authority without seeking unnecessary approval",
      "Takes ownership of outcomes — good and bad",
    ],
    misreadRisk: "Over-managed (treated as S1/S2) — disengages, loses motivation, becomes a flight risk. Turnover of D4 talent is one of the most expensive leadership failures.",
  },
];

export const DEV_MAP = Object.fromEntries(DEV_LEVELS.map(d => [d.id, d]));

export const MATCH_MAP = {
  S1: "D1", S2: "D2", S3: "D3", S4: "D4",
  D1: "S1", D2: "S2", D3: "S3", D4: "S4",
};

export const NORMS = {
  S1: [2, 5], S2: [3, 6], S3: [2, 5], S4: [2, 5],
};
